import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  SimpleChanges,
  OnChanges,
  Output,
  EventEmitter,
  DestroyRef,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { SimulationStateService } from '../../services/simulation-state.service';
import { SimulationTargetMappingService } from '../../services/simulation-target-mapping.service';
import { GlobalService } from '../../services/global.service';
import { SensorCard } from '../../data/enums';
import { ChartBar, DummyDataPoint, UiState } from '../../data/graph';
export interface UiStateHistory {
  state: UiState;
  timestamp: number;
  note: string;
  escalationPhase: string;
}


@Component({
  selector:     'app-amplitude-chart',
  templateUrl:  './amplitude-chart.component.html',
  imports:      [CommonModule, NgbTooltipModule],
  styleUrls:    ['./amplitude-chart.component.scss']
})
export class AmplitudeChartComponent implements OnInit, OnDestroy , OnChanges {

  @Input() animationInterval: number = 10000; 
  @Input() autoStart: boolean = true;
  @Input() maxVisibleBars: number = 60;
  /** Alert rows from parent (DeviceHealth) — chart does not call GetAllFiltered. */
  @Input() chartAlerts: any[] = [];
  
  gridLines: number[] = [0, 12.5, 25, 37.5, 50, 62.5, 75, 87.5];

  allBars: ChartBar[] = [];

  visibleBars: ChartBar[] = [];
  deviceInfoMap: { [macId: string]: any } = {};

  private intervalTimer: any;
  @Input() macId: string = '';
  private pubSubSubscription:any
  @Input() deviceInfo: any;
  @Input() sensorCards: SensorCard[] = [];
  @Output() chartReady = new EventEmitter<void>();
  stateHistory: UiStateHistory[] = [];
  

  private currentBarIndex = 0;

  private baseTimestamp = Date.now() - (120 * 3600 * 1000);

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private simulationState: SimulationStateService,
    private mappingService: SimulationTargetMappingService,
    private globalService: GlobalService
  ) {}

 ngOnInit(): void {
  this.globalService.compactScreen$
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(() => this.calculateBarPositions());

  // Chart data comes from @Input() chartAlerts (parent owns GetAllFiltered).
  // Re-apply bar colors / tooltip types when simulation ON/OFF or targets change.
  this.simulationState.state$
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(() => {
      if (this.simulationState.enabled) {
        this.mappingService.ensureMappingsLoaded().subscribe(() => {
          this.refreshBarsForSimulation();
        });
      } else {
        this.refreshBarsForSimulation();
      }
    });
}


  ngOnDestroy(): void {
  if (this.pubSubSubscription) {
    this.pubSubSubscription.unsubscribe();
  }
}

  get isCompactScreen(): boolean {
    return this.globalService.isCompactScreen;
  }

  shouldShowXTick(index: number): boolean {
    return this.globalService.shouldShowChartXTick(index);
  }

  isLastXTick(index: number): boolean {
    return this.globalService.isLastChartXTick(index, this.visibleBars.length);
  }

  getXTickTransform(index: number): string {
    return this.globalService.getChartXTickTransform(index, this.visibleBars.length);
  }

 ngOnChanges(changes: SimpleChanges): void {
    if (!changes['chartAlerts']) {
      return;
    }

    // Ignore the initial empty binding — wait for DeviceHealth's page-1 payload
    // (including an intentional empty array after fetch).
    const change = changes['chartAlerts'];
    if (
      change.firstChange &&
      (!Array.isArray(change.currentValue) || change.currentValue.length === 0)
    ) {
      return;
    }

    this.loadChartFromAlerts();
  }

  /** Rebuild bars from parent-supplied alert rows (no API call). */
  loadChartFromAlerts(): void {
    // Ensure mappings are ready before building bars when simulation is ON
    // so historical Ammonia (etc.) bars get the correct simulated color/tooltip.
    if (this.simulationState.enabled) {
      this.mappingService.ensureMappingsLoaded().subscribe(() => {
        this.buildBarsFromAlerts(this.chartAlerts);
      });
      return;
    }

    this.buildBarsFromAlerts(this.chartAlerts);
  }

  private buildBarsFromAlerts(alerts: any[]): void {
    // Chart shows ONLY the latest maxVisibleBars (e.g. 60) — never full device history.
    let apiData = Array.isArray(alerts) ? [...alerts] : [];

    // Prefer API `date` (DD/MM/YYYY HH:mm); fall back to timeStamp
    // Newest-first API → chronological (oldest left, newest right) for the chart.
    apiData.sort((a: any, b: any) =>
      (this.getAlertTimeMs(a) ?? 0) - (this.getAlertTimeMs(b) ?? 0)
    );

    // Safety if parent supplies more than the window: keep only the last maxVisibleBars.
    if (apiData.length > this.maxVisibleBars) {
      apiData = apiData.slice(apiData.length - this.maxVisibleBars);
    }

    // Clear old data — bounded buffer only
    this.allBars = [];
    this.visibleBars = [];

    apiData.forEach((item: any) => {

      const timestamp = this.getAlertTimeMs(item);

      // Skip invalid records
      if (timestamp == null || !item.type) return;

      const amplitude = Number(item.amplitude ?? 0);

      // skip only if completely invalid
      if (isNaN(amplitude)) return;

       if (item.macId) {
        this.deviceInfoMap[item.macId] = {
          macId: item.macId,
          floor: item.floorName ?? 'Unknown Floor',
          room: item.friendlyName ?? 'Room N/A'
        };
      }

      const alertId = item.id ?? item.Id ?? undefined;

      const data: DummyDataPoint = {
        amplitude: Number(item.amplitude),
        confidence: this.normalizeConfidence(item.confidence),
        confidenceCurve: Number(item.confidenceCurve ?? 0),
        vocEscalationAmplitude: Number(item.vocEscalationAmplitude ?? 0),
        uiState: item.uiState ?? 'Normal',
        escalationPhase: item.escalationPhase ?? 'Baseline',
        detectionNote: item.detectionNote ?? '',
        type: item.type,
        timestamp: timestamp,
        timeString: this.formatTime(timestamp),
        timeOnlyString: this.formatTimeOnly(timestamp),
        id: alertId != null ? String(alertId) : undefined
      };

      const bar = this.createBar(data, item.macId, item.eventNumber);
      bar.visible = true;

      this.allBars.push(bar);
    });

    this.trimAllBarsToWindow();
    this.updateVisibleBars();
    this.chartReady.emit();
  }

  /** 0–1 scale (e.g. 0.9992) → percent; values already > 1 left as-is. */
  normalizeConfidence(value: number | null | undefined): number {
    const confidence = Number(value ?? 0);
    if (isNaN(confidence)) return 0;
    return confidence <= 1 ? confidence * 100 : confidence;
  }
  /** Prefer API `date` (DD/MM/YYYY HH:mm); fall back to `timeStamp`. */
  private getAlertTimeMs(item: any): number | null {
    const parsed =
      this.globalService.parseAlertDate(item?.date) ??
      this.globalService.parseAlertDate(item?.timeStamp);
    return parsed ? parsed.getTime() : null;
  }

  resetAnimation(): void {
    console.log('🔄 Resetting animation...');
    this.allBars = [];
    this.visibleBars = [];
    this.currentBarIndex = 0;
  }

  /** Same remap rule as Device Map: only when simulation ON + selected target matches. */
  resolveDisplayType(rawType: string): string {
    if (!this.simulationState.enabled) {
      return rawType;
    }

    const resolved = this.mappingService.resolveSimulatedLabel(
      rawType,
      this.simulationState.selectedTargets
    );

    return resolved || rawType;
  }

  getColorForType(type: string): string {
    // Prefer shared palette (chemicals + C-diff); keep chart red slightly brighter for toxigenic
    const key = this.globalService.getAlertTypeKey(type);
    if (key === 'toxigenic') return '#ff3300';
    if (key === 'non-toxigenic') return '#eab308';
    if (key === 'negative') return '#4caf50';
    if (key === 'cleaning') return '#9333ea';
    if (key === 'deep-clean') return '#417399';
    // Keep original Unknown gray (do not use map palette #6c757d)
    if (key === 'unknown') return '#9e9e9e';
    return this.globalService.getAlertTypeColor(type);
  }

  /** Re-apply display type + color from rawType when simulation state changes. */
  private refreshBarsForSimulation(): void {
    if (!this.allBars.length) {
      return;
    }

    this.allBars = this.allBars.map((bar) => {
      const rawType = bar.rawType ?? bar.type;
      const displayType = this.resolveDisplayType(rawType);

      return {
        ...bar,
        rawType,
        type: displayType,
        color: this.getColorForType(displayType)
      };
    });

    this.updateVisibleBars();
  }

  getInferenceText(type: string): string {

    switch(type){

      case 'C-diff Positive Toxigenic':
        return 'C-Diff Positive Toxigenic';

      case 'C-diff Positive non-Toxigenic':
      case 'C-diff Positive Non-Toxigenic':
        return 'C-Diff Positive Non-Toxigenic';

      case 'C-diff Negative':
        return 'C-Diff Negative';

      case 'Cleaning':
        return 'Cleaning Event';

      case 'Deep Clean':
        return 'Deep Clean Event';

      case 'Unknown':
        return 'Unknown Event';

      default:
        return type;
    }

}

  getDeviceInfo(macId: string) {

    // Priority 1: Live device info from parent
    if (this.deviceInfo && this.deviceInfo.macId === macId) {
      return this.deviceInfo;
    }

    // Priority 2: API stored map
    return this.deviceInfoMap[macId] ?? {
      macId: macId,
      floor: 'Unknown Floor',
      room: 'Unknown Room'
    };
  }
  
  createBar(data: DummyDataPoint, deviceId: string , eventNumber?: number): ChartBar {
      // const maxAmplitude = 4.0;
      // const heightPercent = Math.min((data.amplitude / maxAmplitude) * 100, 100);

      // Keep backend/PubSub type; remap only for display (color + tooltip).
      const rawType = data.type || 'Unknown';
      const displayType = this.resolveDisplayType(rawType);
      const color = this.getColorForType(displayType);
    
      // Create the bar object with all properties including tooltipText
      const bar: ChartBar = {
        heightPercent: 0,
        color,
        visible: false,
       amplitude:              data.amplitude,
      // Live sends 0.9992; API path may already be percent — normalize once here
      confidence:             this.normalizeConfidence(data.confidence),
      confidenceCurve:        data.confidenceCurve,
      vocEscalationAmplitude: data.vocEscalationAmplitude, 
      uiState:                data.uiState,
      escalationPhase:        data.escalationPhase,
      detectionNote:          data.detectionNote,
      time:                   data.timeString,
      timeOnly:               data.timeOnlyString,
      type:                   displayType,
      rawType,
      id:                     data.id,
      timestamp:              data.timestamp,
      leftPosition:           0,
      deviceId,
      eventNumber:            eventNumber || this.allBars.length + 1,
        
      };
      
      
      return bar;
  }


  calculateAmplitudeHeights(): void {
    const maxAmplitude = this.getAmplitudeScale();

    this.visibleBars = this.visibleBars.map(bar => {
      const amplitude = Math.abs(Number(bar.amplitude) || 0);

      return {
        ...bar,
        heightPercent: (amplitude / maxAmplitude) * 100
      };
    });
  }

  getAmplitudeScale(): number {
    const maxAmplitude = Math.max(
      ...this.visibleBars.map(
        bar => Math.abs(Number(bar.amplitude)) || 0
      ),
      0
    );

    return Math.max(maxAmplitude, 1);
  }

  // getAmplitudeTicks(): number[] {

  //   const maxAmplitude = this.getAmplitudeScale();

  //   const ticks: number[] = [];

  //   for (let value = maxAmplitude; value >= 0; value -= 0.5) {
  //     ticks.push(value);
  //   }

  //   return ticks;
  // }

  getAmplitudeTicks(): number[] {
    const maxAmplitude = this.getAmplitudeScale();

    const tickCount = 11;
    const step = maxAmplitude / (tickCount - 1);

    return Array.from(
      { length: tickCount },
      (_, index) =>
        Number(
          (maxAmplitude - index * step).toFixed(1)
        )
    );
  }

  /**
   * Sliding window: allBars is the only chart buffer and must stay ≤ maxVisibleBars.
   * Oldest bars are physically removed from memory (not merely hidden).
   */
  private trimAllBarsToWindow(): void {
    if (this.allBars.length <= this.maxVisibleBars) {
      return;
    }
    this.allBars = this.allBars.slice(this.allBars.length - this.maxVisibleBars);
  }

  updateVisibleBars(): void {
    // allBars is already a bounded sliding window — visibleBars mirrors that buffer
    this.allBars.sort(
      (a, b) => a.timestamp - b.timestamp
    );

    this.trimAllBarsToWindow();

    this.visibleBars = [...this.allBars];

    // Recalculate amplitude scale
    this.calculateAmplitudeHeights();

    // Recalculate horizontal positions
    this.calculateBarPositions();
  }

  calculateBarPositions(): void {
    const totalBars = this.visibleBars.length;
    if (!totalBars) return;

    const barWidth = this.getBarWidth();
    const totalWidth = totalBars * barWidth;
    const remaining = Math.max(0, 100 - totalWidth);
    const gaps = totalBars + 1;
    const gapSize = remaining / gaps;

    this.visibleBars.forEach((bar, index) => {

      bar.leftPosition = gapSize + index * (barWidth + gapSize);

    });

  }


  formatTime(timestamp: number): string {
    return new Date(timestamp).toLocaleString('en-US');
  }

  formatTimeOnly(timestamp: number): string {
    return new Date(timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
 

  isRunning(): boolean {
    return this.intervalTimer !== null;
  }

  // Add new bar dynamically (used for PubSub live data)
  public addDataPoint(data: DummyDataPoint | any, deviceId: string): void {
    if (this.simulationState.enabled) {
      this.mappingService.ensureMappingsLoaded().subscribe(() => {
        this.appendDataPoint(data, deviceId);
      });
      return;
    }

    this.appendDataPoint(data, deviceId);
  }

  private appendDataPoint(data: DummyDataPoint | any, deviceId: string): void {
    const alertId =
      data.id != null && data.id !== ''
        ? String(data.id)
        : undefined;

    // Avoid duplicate live/API bars — prefer event id over timestamp
    if (alertId && this.allBars.some((b) => b.id === alertId)) {
      return;
    }

    // Use incoming timestamp OR fallback
    let newTimestamp = data.timestamp || Date.now();

    // FIX: Ensure UNIQUE timestamp (prevents skip issue)
    while (this.allBars.some(b => b.timestamp === newTimestamp)) {
      newTimestamp += 1;
    }

    const newBar = this.createBar(
      {
        amplitude: Number(data.amplitude ?? 0),
        confidence: this.normalizeConfidence(data.confidence),
        confidenceCurve: Number(data.confidenceCurve ?? 0),
        vocEscalationAmplitude: Number(data.vocEscalationAmplitude ?? 0),
        uiState: data.uiState ?? 'Normal',
        escalationPhase: data.escalationPhase ?? 'Baseline',
        detectionNote: data.detectionNote ?? '',
        type: data.type || 'Unknown',
        timestamp: newTimestamp,
        timeString: this.formatTime(newTimestamp),
        timeOnlyString: this.formatTimeOnly(newTimestamp),
        id: alertId
      },
      deviceId
    );

    // STEP 1: Add hidden first (for animation)
    newBar.visible = false;

    // STEP 2: Append into sliding window (trim happens in updateVisibleBars after sort)
    this.allBars = [...this.allBars, newBar];

    this.updateVisibleBars();

    // STEP 3: Animate after render
    setTimeout(() => {
      newBar.visible = true;
      this.updateVisibleBars();
    }, 50);
  }

  
  getBarLeftPosition(bar: ChartBar): number {
    return bar.leftPosition;
  }

  getBarWidth(): number {
    return this.globalService.getChartBarWidthPercent();
  }

  /**
   * Map confidence (0–100) to SVG Y.
   * Top padding keeps 100% markers fully visible (not clipped at cy=0).
   */
  getConfidenceY(confidence: number): number {
    const value = Math.min(100, Math.max(0, Number(confidence) || 0));
    const topPad = 4;
    const usable = 100 - topPad;
    return topPad + usable * (1 - value / 100);
  }

  getConfidenceLinePoints(): string {

    return this.visibleBars
      .map(bar => {

        const x = bar.leftPosition + this.getBarWidth() / 2;
        const y = this.getConfidenceY(bar.confidence);

        return `${x},${y}`;

      })
      .join(' ');

  }

  getAlertCount(): number {
    return this.visibleBars.filter(bar => bar.uiState === 'Critical').length;
  }

  getVisibleBarCount(): number {
    return this.visibleBars.filter(bar => bar.visible).length;
  }

  // ─────────────────────────────────────────
  // PUBLIC API METHODS
  // ─────────────────────────────────────────

  public getAllData(): ChartBar[] {
    return [...this.allBars];
  }

  public clearData(): void {
    this.allBars = [];
    this.visibleBars = [];
    this.currentBarIndex = 0;
  }

  public getProgress(): number {
    return this.allBars.length > 0 ? Math.round((this.allBars.length / 100) * 100) : 0;
  }


   getCurrentUiState(): UiState {
    const visible = this.visibleBars.filter(b => b.visible);
    if (!visible.length) return 'Normal';
    return visible[visible.length - 1].uiState;
  }
    
  isNewDate(index: number): boolean {

    if (index === 0) return true;

    const current = new Date(this.visibleBars[index].timestamp);
    const prev = new Date(this.visibleBars[index - 1].timestamp);

    return current.toDateString() !== prev.toDateString();
  }

  formatDate(timestamp: number): string {
    return new Date(timestamp).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short'
    });
  }

  hasType(...types: string[]): boolean {
    return this.visibleBars.some((bar) =>
      this.globalService.alertTypeMatches(bar.type, ...types)
    );
  }

  /** Legend label for a visible chemical / alert type key. */
  getLegendLabel(typeKey: string): string {
    const bar = this.visibleBars.find(
      (b) => this.globalService.getAlertTypeKey(b.type) === typeKey
    );
    return bar?.type || typeKey;
  }

  getLegendColor(typeKey: string): string {
    return this.getColorForType(typeKey);
  }

  hasTypeKey(typeKey: string): boolean {
    return this.visibleBars.some(
      (bar) => this.globalService.getAlertTypeKey(bar.type) === typeKey
    );
  }


  updateStateHistory(bar: ChartBar): void {

    // const lastState =
    //   this.stateHistory.length > 0
    //     ? this.stateHistory[this.stateHistory.length - 1].state
    //     : null;
    //     debugger

    // if (lastState !== bar.uiState) {

      this.stateHistory.unshift({
        state: bar.uiState,
        timestamp: bar.timestamp,
        note: bar.detectionNote,
        escalationPhase: bar.escalationPhase
      });

    // }

  }


   getCurrentUiMessage(): string {

    const visible = this.visibleBars.filter(b => b.visible);

    if (!visible.length) {
      return 'No active environmental events';
    }

    return visible[visible.length - 1].detectionNote;
  }

  getCurrentEscalationPhase(): string {

    const visible = this.visibleBars.filter(b => b.visible);

    if (!visible.length) {
      return 'Baseline';
    }

    return visible[visible.length - 1].escalationPhase;


  }

}